import { getClassOptionLabel } from "../../utils/classPresentation";
import ClassPresentation from "../../Components/ClassPresentation";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FiChevronLeft, FiChevronRight, FiClock, FiRefreshCw, FiSearch, FiSliders } from "react-icons/fi";
import { Link, useSearchParams } from "react-router-dom";
import Swal from "sweetalert2";
import MenuLateral from "../../Components/MenuLateral";
import { APP_ROUTES } from "../../auth/access";
import api from "../../services/api";
import { getRecognitionHealthPresentation } from "../../services/recognitionStatus";
import "./index.css";

const PAGE_SIZE = 10;

const DEFAULT_FILTERS = {
  studentName: "",
  institutionId: "",
  classId: "",
  order: "DESC",
};

const REQUEST_STATUS_PRESENTATIONS = {
  REQUESTED: { label: "Solicitada", tone: "requested" },
  PROCESSING: { label: "Processando", tone: "processing" },
  COMPLETED: { label: "Concluída", tone: "completed" },
  FAILED: { label: "Falhou", tone: "failed" },
};

const getErrorMessage = (error, fallback) => {
  const message = error?.response?.data?.message;
  if (Array.isArray(message)) return message.join(", ");
  return typeof message === "string" && message.trim() ? message : fallback;
};

const formatDateTime = (value) => {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  return new Intl.DateTimeFormat("pt-BR", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(date);
};


const getRequestStatusPresentation = (status) =>
  REQUEST_STATUS_PRESENTATIONS[status] || { label: "Status atualizado", tone: "pending" };

function RecognitionPagination({ page, totalPages, total, loading, onChange }) {
  if (totalPages <= 1) return null;

  return (
    <nav className="recognition-pagination" aria-label="Paginação das solicitações">
      <span>
        Página <strong>{page}</strong> de <strong>{totalPages}</strong>
        <small>{total} resultado(s)</small>
      </span>
      <div className="recognition-pagination-actions">
        <button
          type="button"
          onClick={() => onChange(page - 1)}
          disabled={loading || page <= 1}
          aria-label="Página anterior"
        >
          <FiChevronLeft aria-hidden="true" />
          <span>Anterior</span>
        </button>
        <button
          type="button"
          onClick={() => onChange(page + 1)}
          disabled={loading || page >= totalPages}
          aria-label="Próxima página"
        >
          <span>Próxima</span>
          <FiChevronRight aria-hidden="true" />
        </button>
      </div>
    </nav>
  );
}

function RecognitionOperations() {
  const [searchParams] = useSearchParams();
  const eventId = searchParams.get("eventId") || "";
  const [summary, setSummary] = useState(null);
  const [requests, setRequests] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: PAGE_SIZE,
    total: 0,
    totalPages: 0,
  });
  const [draftFilters, setDraftFilters] = useState(DEFAULT_FILTERS);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [page, setPage] = useState(1);
  const [filterOptions, setFilterOptions] = useState({ institutions: [], classes: [] });
  const [filterOptionsLoading, setFilterOptionsLoading] = useState(true);
  const [filterOptionsError, setFilterOptionsError] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionId, setActionId] = useState("");

  const filteredClasses = useMemo(() => {
    if (!draftFilters.institutionId) return filterOptions.classes;

    return filterOptions.classes.filter((schoolClass) =>
      schoolClass.institutionId === draftFilters.institutionId ||
      schoolClass.institution?.id === draftFilters.institutionId,
    );
  }, [draftFilters.institutionId, filterOptions.classes]);

  const loadFilterOptions = useCallback(async () => {
    setFilterOptionsLoading(true);
    setFilterOptionsError("");

    const [institutionsResult, classesResult] = await Promise.allSettled([
      api.get("/institution"),
      api.get("/class"),
    ]);

    const institutions = institutionsResult.status === "fulfilled"
      ? institutionsResult.value.data?.institutions || []
      : [];
    const classes = classesResult.status === "fulfilled"
      ? classesResult.value.data?.classes || []
      : [];

    setFilterOptions({ institutions, classes });
    if (institutionsResult.status === "rejected" || classesResult.status === "rejected") {
      setFilterOptionsError("Instituições ou turmas não puderam ser carregadas. A busca por aluno continua disponível.");
    }
    setFilterOptionsLoading(false);
  }, []);

  const loadOperations = useCallback(async () => {
    setLoading(true);
    setError("");

    const requestParams = {
      page,
      limit: PAGE_SIZE,
      order: filters.order,
    };

    if (eventId) requestParams.eventId = eventId;
    if (filters.studentName) requestParams.studentName = filters.studentName;
    if (filters.institutionId) requestParams.institutionId = filters.institutionId;
    if (filters.classId) requestParams.classId = filters.classId;

    try {
      const [summaryResponse, requestsResponse] = await Promise.all([
        api.get("/recognition/operations/summary", {
          params: eventId ? { eventId } : undefined,
        }),
        api.get("/recognition/reprocess-requests", { params: requestParams }),
      ]);

      setSummary(summaryResponse.data || null);
      setRequests(requestsResponse.data?.requests || []);
      setPagination(requestsResponse.data?.pagination || {
        page,
        limit: PAGE_SIZE,
        total: requestsResponse.data?.requests?.length || 0,
        totalPages: 1,
      });
    } catch (requestError) {
      setSummary(null);
      setRequests([]);
      setPagination({ page: 1, limit: PAGE_SIZE, total: 0, totalPages: 0 });
      setError(getErrorMessage(requestError, "Não foi possível carregar a aprovação de análise facial."));
    } finally {
      setLoading(false);
    }
  }, [eventId, filters, page]);

  useEffect(() => {
    loadFilterOptions();
  }, [loadFilterOptions]);

  useEffect(() => {
    loadOperations();
  }, [loadOperations]);

  const applyFilters = (event) => {
    event.preventDefault();
    setFilters({
      ...draftFilters,
      studentName: draftFilters.studentName.trim(),
    });
    setPage(1);
  };

  const clearFilters = () => {
    setDraftFilters(DEFAULT_FILTERS);
    setFilters(DEFAULT_FILTERS);
    setPage(1);
  };

  const updateInstitution = (event) => {
    setDraftFilters((current) => ({
      ...current,
      institutionId: event.target.value,
      classId: "",
    }));
  };

  const startRequest = async (request) => {
    const confirmation = await Swal.fire({
      title: "Iniciar solicitação?",
      text: "Somente fotos sem match atual para o aluno serão reenfileiradas.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Iniciar",
      cancelButtonText: "Cancelar",
    });
    if (!confirmation?.isConfirmed) return;

    setActionId(request.id);
    try {
      await api.post(`/recognition/reprocess-requests/${request.id}/start`);
      await loadOperations();
      Swal.fire("Solicitação iniciada", "O resultado será atualizado pelo estado persistido.", "success");
    } catch (requestError) {
      Swal.fire("Não foi possível iniciar", getErrorMessage(requestError, "Atualize a aprovação antes de tentar novamente."), "error");
    } finally {
      setActionId("");
    }
  };

  const health = summary?.health || {};
  const healthPresentation = getRecognitionHealthPresentation(health.status);
  const currentPage = pagination.page || page;
  const totalPages = Math.max(pagination.totalPages || 0, 1);
  const hasAppliedFilters = Boolean(filters.studentName || filters.institutionId || filters.classId || eventId);
  const requestCount = pagination.total || 0;

  return (
    <div className="pagina-recognition-operations">
      <MenuLateral />
      <main className="recognition-operations-content">
        <header className="recognition-operations-header">
          <div className="recognition-operations-heading">
            <span className="recognition-eyebrow">Central administrativa · reconhecimento</span>
            <h1>Aprovação de análise facial</h1>
            <p>Localize uma solicitação, confira o contexto escolar e decida quando iniciar o processamento.</p>
            <small>{eventId ? "Recorte do evento atual." : "Todos os eventos da sua empresa."}</small>
          </div>
          <div className="recognition-operations-actions">
            {eventId ? <Link to={APP_ROUTES.RECOGNITION_OPERATIONS}>Ver todos</Link> : null}
            <button type="button" onClick={loadOperations} disabled={loading || Boolean(actionId)}>
              <FiRefreshCw aria-hidden="true" />
              {loading ? "Atualizando..." : "Atualizar"}
            </button>
          </div>
        </header>

        {error ? (
          <section className="recognition-operations-state error" role="alert">
            <p>{error}</p>
            <button type="button" onClick={loadOperations}>Tentar novamente</button>
          </section>
        ) : null}

        {loading && !summary ? (
          <div className="recognition-operations-state" role="status" aria-busy="true">
            Carregando a aprovação de análise facial...
          </div>
        ) : null}

        {summary ? (
          <>
            <section className={`recognition-health-card ${healthPresentation.tone}`}>
              <div className="recognition-health-copy">
                <span className="recognition-card-kicker">Sinal do serviço</span>
                <strong>{healthPresentation.label}</strong>
                <p>O estado é consultado junto com a fila persistida.</p>
              </div>
              <dl>
                <div>
                  <dt>Última conferência</dt>
                  <dd>{formatDateTime(health.checkedAt)}</dd>
                </div>
                <div>
                  <dt>Solicitações na busca</dt>
                  <dd>{requestCount}</dd>
                </div>
              </dl>
            </section>

            <section className="recognition-filter-panel" aria-labelledby="recognition-filter-title">
              <div className="recognition-section-heading">
                <div>
                  <span className="recognition-eyebrow">Encontre pelo contexto</span>
                  <h2 id="recognition-filter-title">Buscar solicitações</h2>
                  <p>Use o nome do aluno ou refine pela instituição e pela turma.</p>
                </div>
                <FiSliders className="recognition-section-mark" aria-hidden="true" />
              </div>

              <form className="recognition-filter-form" onSubmit={applyFilters}>
                <div className="recognition-field recognition-search-field">
                  <label htmlFor="recognition-student-search">Aluno</label>
                  <div className="recognition-input-wrap">
                    <FiSearch aria-hidden="true" />
                    <input
                      id="recognition-student-search"
                      type="search"
                      value={draftFilters.studentName}
                      onChange={(event) => setDraftFilters((current) => ({ ...current, studentName: event.target.value }))}
                      placeholder="Digite o nome do aluno"
                      autoComplete="off"
                    />
                  </div>
                </div>

                <div className="recognition-field">
                  <label htmlFor="recognition-institution-filter">Instituição</label>
                  <select
                    id="recognition-institution-filter"
                    value={draftFilters.institutionId}
                    onChange={updateInstitution}
                    disabled={filterOptionsLoading}
                  >
                    <option value="">Todas as instituições</option>
                    {filterOptions.institutions.map((institution) => (
                      <option key={institution.id} value={institution.id}>{institution.name}</option>
                    ))}
                  </select>
                </div>

                <div className="recognition-field">
                  <label htmlFor="recognition-class-filter">Turma</label>
                  <select
                    id="recognition-class-filter"
                    value={draftFilters.classId}
                    onChange={(event) => setDraftFilters((current) => ({ ...current, classId: event.target.value }))}
                    disabled={filterOptionsLoading}
                  >
                    <option value="">Todas as turmas</option>
                    {filteredClasses.map((schoolClass) => (
                      <option key={schoolClass.id} value={schoolClass.id}>{getClassOptionLabel(schoolClass)}</option>
                    ))}
                  </select>
                </div>

                <div className="recognition-field">
                  <label htmlFor="recognition-order-filter">Ordenar por data</label>
                  <select
                    id="recognition-order-filter"
                    value={draftFilters.order}
                    onChange={(event) => setDraftFilters((current) => ({ ...current, order: event.target.value }))}
                  >
                    <option value="DESC">Mais recentes primeiro</option>
                    <option value="ASC">Mais antigas primeiro</option>
                  </select>
                </div>

                <div className="recognition-filter-actions">
                  <button type="submit" className="recognition-primary-button" disabled={loading}>
                    <FiSearch aria-hidden="true" />
                    Buscar
                  </button>
                  <button type="button" className="recognition-secondary-button" onClick={clearFilters} disabled={loading}>
                    Limpar
                  </button>
                </div>
              </form>

              {filterOptionsError ? <p className="recognition-filter-note" role="status">{filterOptionsError}</p> : null}
            </section>

            <section className="recognition-operations-section" aria-labelledby="recognition-requests-title" aria-busy={loading}>
              <div className="recognition-operations-section-header">
                <div>
                  <span className="recognition-eyebrow">Fila de decisão</span>
                  <h2 id="recognition-requests-title">Solicitações de alunos</h2>
                  <p>O pedido aguarda decisão do ADMIN e não executa a análise facial diretamente.</p>
                </div>
                <span className="recognition-list-count">{requestCount} {requestCount === 1 ? "solicitação" : "solicitações"}</span>
              </div>

              {loading ? <div className="recognition-inline-status" role="status">Atualizando a fila...</div> : null}

              <div className="recognition-job-list">
                {requests.map((request) => {
                  const statusPresentation = getRequestStatusPresentation(request.status);
                  return (
                    <article key={request.id} className={`recognition-job-card request ${statusPresentation.tone}`}>
                      <div className="recognition-job-main">
                        <div className="recognition-job-topline">
                          <span className={`recognition-status-pill ${statusPresentation.tone}`}>
                            <span aria-hidden="true" />
                            {statusPresentation.label}
                          </span>
                          <span className="recognition-job-date"><FiClock aria-hidden="true" />{formatDateTime(request.requestedAt)}</span>
                        </div>
                        <p className="recognition-job-student">
                          <span>Aluno solicitante:</span>
                          <strong>{request.studentName || "Nome não informado"}</strong>
                        </p>
                        <p className="recognition-job-context">
                          <ClassPresentation schoolClass={request} />
                          <span>{request.eventName || "Evento não informado"}</span>
                        </p>
                        <p className="recognition-job-message">{request.lastMessage || "Sem mensagem operacional adicional."}</p>
                        <small>{request.completedPhotos || 0}/{request.eligiblePhotos || 0} foto(s) concluídas · {request.failedPhotos || 0} com falha</small>
                      </div>
                      <div className="recognition-job-meta">
                        {request.status === "REQUESTED" ? (
                          <button type="button" onClick={() => startRequest(request)} disabled={Boolean(actionId) || loading}>
                            {actionId === request.id ? "Iniciando..." : "Iniciar"}
                          </button>
                        ) : <span>O estado persistido será atualizado ao terminar.</span>}
                      </div>
                    </article>
                  );
                })}
                {requests.length === 0 ? (
                  <div className="recognition-operations-empty">
                    <strong>Nenhuma solicitação encontrada</strong>
                    <p>{hasAppliedFilters ? "Tente ajustar os filtros para ampliar a busca." : "Quando um aluno solicitar uma nova análise, o pedido aparecerá aqui."}</p>
                    {hasAppliedFilters ? <button type="button" onClick={clearFilters}>Limpar filtros</button> : null}
                  </div>
                ) : null}
              </div>

              <RecognitionPagination
                page={currentPage}
                totalPages={totalPages}
                total={requestCount}
                loading={loading}
                onChange={setPage}
              />
            </section>
          </>
        ) : null}
      </main>
    </div>
  );
}

export default RecognitionOperations;
